import { useState, useEffect, useCallback } from 'react';
import Button from '@/trio-ui/Button';
import { PersonType } from '@/types/api';
import { onScrollToSmoothly } from '@/utils/global';
import { PageGrid, Sidebar, Content, SaveBar } from './PersonalInfoPageContent.styles';
import PersonalInfoAddressForm from '../PersonalInfoAddressForm';
import PersonalInfoBasicInfoForm from '../PersonalInfoBasicInfoForm';
import PersonalInfoContactForm from '../PersonalInfoContactForm';
import PersonalInfoEmergencyForm from '../PersonalInfoEmergencyForm';
import SectionBox from '../SectionBox';

export type Props = {
  person?: PersonType;
  onSubmit?: (data: Partial<PersonType>) => Promise<void> | void;
  isMyPersonalInfo?: boolean;
};

type FormState = {
  firstName: string;
  lastName: string;
  birthdate: string;
  gender: string;
  employeeWorkStatus: string;
  ethnicity: string;
  email: string;
  workEmail: string;
  mobilePhone: string;
  linkedinUrl: string;
  addressLine1: string;
  addressLine2: string;
  zipCode: string;
  city: string;
  state: string;
  country: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactEmail: string;
  emergencyContactRelationship: string;
};

function buildFormState(person?: PersonType): FormState {
  return {
    firstName: person?.firstName ?? '',
    lastName: person?.lastName ?? '',
    birthdate: person?.birthdate ?? '',
    gender: person?.gender ?? '',
    employeeWorkStatus: person?.employeeWorkStatus ?? '',
    ethnicity: person?.ethnicity ?? '',
    email: person?.email ?? '',
    workEmail: person?.workEmail ?? '',
    mobilePhone: person?.mobilePhone ?? '',
    linkedinUrl: person?.linkedinUrl ?? '',
    addressLine1: person?.addressLine1 ?? '',
    addressLine2: person?.addressLine2 ?? '',
    zipCode: person?.zipCode ?? '',
    city: person?.city ?? '',
    state: person?.state ?? '',
    country: person?.country ?? '',
    emergencyContactName: person?.emergencyContactName ?? '',
    emergencyContactPhone: person?.emergencyContactPhone ?? '',
    emergencyContactEmail: person?.emergencyContactEmail ?? '',
    emergencyContactRelationship: person?.emergencyContactRelationship ?? '',
  };
}

const PersonalInfoPageContent = ({ person, onSubmit, isMyPersonalInfo }: Props): JSX.Element => {
  const [form, setForm] = useState<FormState>(() => buildFormState(person));
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setForm(buildFormState(person));
    setIsDirty(false);
  }, [person]);

  const handleChange = useCallback((name: string, value: any) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setIsDirty(true);
  }, []);

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    try {
      await onSubmit?.(form as any);
      setIsDirty(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageGrid>
      <Sidebar
        sidebarMenus={[
          {
            label: 'Basic Info',
            id: 'sidebar-basic-info',
            onClick: () => onScrollToSmoothly('basic-info'),
          },
          {
            label: 'Contact',
            id: 'sidebar-contact',
            onClick: () => onScrollToSmoothly('contact'),
          },
          {
            label: 'Address',
            id: 'sidebar-address',
            onClick: () => onScrollToSmoothly('address'),
          },
          {
            label: 'Emergency',
            id: 'sidebar-emergency',
            onClick: () => onScrollToSmoothly('emergency'),
          },
        ]}
      />
      <Content>
        <SectionBox title="Basic Info" id="basic-info" className="!tw-pb-[4px]">
          <PersonalInfoBasicInfoForm
            firstName={form.firstName}
            lastName={form.lastName}
            birthdate={form.birthdate}
            gender={form.gender}
            employeeWorkStatus={form.employeeWorkStatus}
            ethnicity={form.ethnicity}
            onChange={handleChange}
            isMyPersonalInfo={isMyPersonalInfo}
          />
        </SectionBox>
        <SectionBox title="Contact" id="contact" className="!tw-pb-[4px]">
          <PersonalInfoContactForm
            email={form.email}
            workEmail={form.workEmail}
            mobilePhone={form.mobilePhone}
            linkedinUrl={form.linkedinUrl}
            onChange={handleChange}
            isMyPersonalInfo={isMyPersonalInfo}
          />
        </SectionBox>
        <SectionBox title="Address" id="address" className="!tw-pb-[4px]">
          <PersonalInfoAddressForm
            addressLine1={form.addressLine1}
            addressLine2={form.addressLine2}
            zipCode={form.zipCode}
            city={form.city}
            state={form.state}
            country={form.country}
            onChange={handleChange}
          />
        </SectionBox>
        <SectionBox title="Emergency" id="emergency" className="!tw-pb-[4px]">
          <PersonalInfoEmergencyForm
            emergencyContactName={form.emergencyContactName}
            emergencyContactPhone={form.emergencyContactPhone}
            emergencyContactEmail={form.emergencyContactEmail}
            emergencyContactRelationship={form.emergencyContactRelationship}
            onChange={handleChange}
          />
        </SectionBox>
        <SaveBar>
          <Button
            onClick={handleSave}
            disabled={!isDirty || isSaving}
            size="medium"
            variantColor="blue"
            variantStyle="filled"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </SaveBar>
      </Content>
    </PageGrid>
  );
};

export default PersonalInfoPageContent;
